import { Language } from "../i18n/languages";
import { FeatureName, ModuleName } from "./product";
import { CompanyItem, UserItem } from "./common";

export type CustomRoleItem = {
  role: CustomRole;
  users: UserItem[];
  availableUsers: UserItem[];
  companies: CompanyItem[];
};

export type CustomRole = {
  customRoleId: string;
  orgNo: string;
  customRoleName: string;
  modules: ModuleName[];
  features: AccessToFeature[];
  customRoleLanguages: CustomRoleLanguage[];
};

export type CustomRoleLanguage = {
  customRoleId: string;
  language: Language;
  name: string;
  description?: string;
};

export type CustomRoleForUser = {
  customRoleId: string;
  orgNo: string;
  companiesUsingCustomRole: string[];
  customRoleName: string;
  modules: ModuleName[];
  features: FeatureName[];
  allowOverview?: boolean;
  allowShareholderRegister?: boolean;
  allowDbot?: boolean;
  allowArchive?: boolean;
  allowMessaging?: boolean;
  allowSignatures?: boolean;

  //overview
  allowDashboardView?: boolean;
  allowArticlesEdit?: boolean;
  allowArticlesView?: boolean;
  allowPoAsEdit?: boolean;
  allowPoAsView?: boolean;
  allowOwnershipView?: boolean;
  allowCompanyRolesEdit?: boolean;
  allowCompanyRolesView?: boolean;
  allowCompanySettingsEdit?: boolean;
  allowCompanySettingsView?: boolean;

  //shareholder register
  allowShareholdersEdit?: boolean;
  allowShareholdersView?: boolean;
  allowTransactionsEdit?: boolean;
  allowTransactionsView?: boolean;
  allowShareClassEdit?: boolean;
  allowShareClassView?: boolean;
  allowCommitmentsEdit?: boolean;
  allowCommitmentsView?: boolean;
  allowShareholderRegisterStatementEdit?: boolean;
  allowShareholderRegisterStatementView?: boolean;
  allowBeneficialOwnersEdit?: boolean;
  allowBeneficialOwnersView?: boolean;
  allowSignaturesView?: boolean;

  //dbot

  //archive
  allowBoardArchiveEdit?: boolean;
  allowBoardArchiveView?: boolean;
  allowShareholderArchiveEdit?: boolean;
  allowShareholderArchiveView?: boolean;
  allowAdminArchiveEdit?: boolean;
  allowAdminArchiveView?: boolean;
  allowCustomArchiveEdit?: boolean;
  allowCustomArchiveView?: boolean;
  allowBoardMessagesEdit?: boolean;
  allowBoardMessagesView?: boolean;
  allowShareholderMessagesEdit?: boolean;
  allowShareholderMessagesView?: boolean;
};

export type FeatureAccess = "MANAGE" | "EDIT" | "VIEW" | "NONE";
export const FeatureAccessValues: FeatureAccess[] = ["MANAGE", "EDIT", "VIEW", "NONE"];

export type AccessToFeature = {
  featureName: FeatureName;
  access: FeatureAccess;
};

export type NewCustomRoleRequest = {
  roleName: string;
  roleLanguages: CustomRoleLanguage[];
  enabledModules: string[];
  featureAccessList: AccessToFeature[];
  companiesUsingRole: string[];
  isShared: boolean;
};

export type UpdateCustomRoleRequest = NewCustomRoleRequest & {
  customRoleId: string;
};

export const allowNone: CustomRoleForUser = {
  orgNo: "none",
  customRoleId: "none",
  customRoleName: "none",
  modules: [],
  companiesUsingCustomRole: [],
  features: [],
};

export const allowAll: CustomRoleForUser = {
  customRoleId: "all",
  customRoleName: "all",
  orgNo: "all",
  companiesUsingCustomRole: [],
  modules: Object.values(ModuleName),
  features: Object.values(FeatureName),
  //modules
  allowOverview: true,
  allowShareholderRegister: true,
  allowDbot: true,
  allowArchive: true,
  allowMessaging: true,

  //overview
  allowDashboardView: true,
  allowArticlesEdit: true,
  allowArticlesView: true,
  allowPoAsEdit: true,
  allowPoAsView: true,
  allowOwnershipView: true,
  allowCompanyRolesEdit: true,
  allowCompanyRolesView: true,
  allowCompanySettingsEdit: true,
  allowCompanySettingsView: true,

  //shareholder register
  allowShareholdersEdit: true,
  allowShareholdersView: true,
  allowTransactionsEdit: true,
  allowTransactionsView: true,
  allowShareClassEdit: true,
  allowShareClassView: true,
  allowCommitmentsEdit: true,
  allowCommitmentsView: true,
  allowShareholderRegisterStatementEdit: true,
  allowShareholderRegisterStatementView: true,
  allowBeneficialOwnersEdit: true,
  allowBeneficialOwnersView: true,

  //dbot

  //archive
  allowBoardArchiveEdit: true,
  allowBoardArchiveView: true,
  allowShareholderArchiveEdit: true,
  allowShareholderArchiveView: true,
  allowAdminArchiveEdit: true,
  allowAdminArchiveView: true,
  allowCustomArchiveEdit: true,
  allowCustomArchiveView: true,
  allowBoardMessagesEdit: true,
  allowBoardMessagesView: true,
  allowShareholderMessagesEdit: true,
  allowShareholderMessagesView: true,
};

export const mergeRoles = (roles: CustomRoleForUser[], orgNo: string): CustomRoleForUser => {
  const rolesForThisCompany = roles.filter(
    (r) => r.orgNo === orgNo || r.companiesUsingCustomRole.includes(orgNo),
  );
  const modules = rolesForThisCompany.reduce<ModuleName[]>(
    (acc, curr) => [...acc, ...curr.modules],
    [],
  );
  const features = rolesForThisCompany.reduce<FeatureName[]>(
    (acc, curr) => [...acc, ...curr.features],
    [],
  );
  const mergedFeatureFlags = rolesForThisCompany.reduce<CustomRoleForUser>((acc, curr) => {
    return Object.keys({ ...acc, ...curr }).reduce((acc, key) => {
      const prop = key as keyof CustomRoleForUser;
      if (
        (typeof curr[prop] === "boolean" || typeof curr[prop] === "undefined") &&
        (typeof acc[prop] === "boolean" || typeof acc[prop] === "undefined")
      ) {
        (acc[prop] as boolean | undefined) = acc[prop] || curr[prop];
      }
      return acc;
    }, allowNone);
  }, allowNone);

  return {
    ...mergedFeatureFlags,
    customRoleId: "merged",
    customRoleName: "Merged",
    modules: Object.values(ModuleName).filter((m) => modules.includes(m)),
    features: Object.values(FeatureName).filter((f) => features.includes(f)),
  };
};
